import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Home, X, Check, Edit2, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
// Cross-domain imports — Andrew override 2026-05-20 (PROD-841): the
// address + callback-contact editors are shared concerns between the
// patient-chart Edit Patient flow and this appointment-context picker.
// Per-line guardrail comments below so a future reviewer doesn't
// remove these as "scheduling shouldn't import from patients" lint
// violations. Both lines are sanctioned exceptions to CLAUDE.md's
// "never cross-import between domains" rule.
// cross-domain: approved 2026-05-20 PROD-841
import { EditResidentialAddressView } from '@/patients/components/contact-details/edit-residential-address-view';
// cross-domain: approved 2026-05-20 PROD-841
import { EditCallBackContactView } from '@/patients/components/emergency-contacts/edit-callback-contact-view';
import {
  usePreBookSelectedPatient,
  toInvoicePrices,
} from '@/services/scheduling/use-prebook-selected-patient';
import type { AppointmentFormValues } from '@/types/appointment-form';

type AddressListItem = AppointmentFormValues['addressList'][number];

type ViewMode =
  | { kind: 'picker' }
  | { kind: 'edit-address'; selectedAddressId: string | null }
  | { kind: 'edit-callback'; emergencyContactId: string };

/**
 * ResidentialAddressSection — picks the patient address to visit.
 * Mirrors Flutter `residential_address_widget.dart` (picker) +
 * `calendar_screen.dart:802-843` (routing rule to the sub-views).
 *
 * Visible only when `location.name === 'Home'` AND a patient is loaded.
 *
 * Picker behaviour:
 *   - List sourced from `addressList` (seeded by `patient-section.tsx`
 *     from `PreBookSelectedPatient.patientAddressList`, Shape A).
 *   - "Add Address" header button opens the address sub-view in add mode.
 *   - Edit icon on each card opens EITHER the address sub-view OR the
 *     callback-contact sub-view based on `isResidential` (Flutter
 *     `calendar_screen.dart:803-822`):
 *       residentialAddress == null || isResidential === true
 *         → EditResidentialAddressView
 *       else (the picked entry is an emergency contact)
 *         → EditCallBackContactView
 *
 * Save flow:
 *   - Modal saves commit immediately via PUT /api/Patients/{id}
 *     (address) or POST /api/Patients/EmergencyContact (callback).
 *   - After 200, this component re-fires `PreBookSelectedPatient` so
 *     the picker re-seeds with the fresh merged Shape A list. This is
 *     a deliberate divergence from literal Flutter (which leaves the
 *     unselected list stale until something else triggers refresh) —
 *     locked with Andrew 2026-05-20.
 *
 * Title-source rule:
 *   - `isResidential === true` → bold `locationName`
 *   - else → bold `name` (emergency contact's full name)
 *
 * Empty-fullAddress filter mirrors Flutter `scheduling_bloc.dart:973-977`.
 */
export function ResidentialAddressSection() {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const location = watch('location');
  const patientId = watch('patientId');
  const addressList = watch('addressList');
  const selectedId = watch('residentialAddressId');

  // Watched params needed to re-fire PreBookSelectedPatient on save.
  const providerId = watch('providerId');
  const billingProfileId = watch('billingProfileId');
  const sponsorType = watch('sponsorType');

  const preBookPatient = usePreBookSelectedPatient();
  const [view, setView] = useState<ViewMode>({ kind: 'picker' });

  if (location?.name !== 'Home' || !patientId) return null;

  const visibleAddresses = addressList.filter(
    (addr) => formatAddress(addr).length > 0,
  );

  const select = (id: string) => {
    setValue('residentialAddressId', id, { shouldValidate: true });
  };

  /**
   * Re-fire PreBookSelectedPatient with the same params the
   * patient-section used on initial pick. Re-seeds `addressList` (and
   * sibling form data — invoice / residentialAddress) from the fresh
   * merged Shape A response.
   *
   * Per Samer's PR #246 review:
   *   - `onError` handler surfaces a user-visible toast (otherwise the
   *     UI state and the saved data diverge silently — save toast says
   *     "saved" but the picker still shows the old list).
   *   - Loading affordance ("Refreshing addresses…") rendered while
   *     the mutation is pending — see `preBookPatient.isPending` in
   *     the picker section below — so the user doesn't get a stale
   *     window between save toast and picker refresh.
   */
  const refreshFromPreBook = () => {
    if (!providerId || !location?.id) return;
    preBookPatient.mutate(
      {
        patientId,
        providerId,
        locationId: location.id,
        billingProfileId,
        paymentType:
          (sponsorType ?? 'Self-Pay') === 'Self-Pay'
            ? 'Self-pay'
            : (sponsorType as 'Insurance' | 'Corporate'),
      },
      {
        onSuccess: (response) => {
          const rawAddresses = response.patientAddressList ?? [];
          const addresses = rawAddresses.filter(
            (a) =>
              [a.apartment, a.address, a.city, a.country]
                .map((p) => (p ?? '').trim())
                .some((p) => p.length > 0),
          );
          setValue('addressList', addresses);
          const def = addresses.find((a) => a.isDefault) ?? addresses[0];
          const defId = def?.id ?? null;
          if (def) setValue('residentialAddress', def);
          if (defId)
            setValue('residentialAddressId', defId, { shouldValidate: true });
          const normalised = toInvoicePrices(response.prices);
          if (normalised) setValue('invoiceTotals', normalised);
        },
        onError: () => {
          // The PUT already committed; the picker just can't reflect it
          // until something else triggers a refresh. The global
          // QueryClient onError handler also surfaces the network
          // failure as a toast — this message is the user-visible
          // explanation specific to this surface.
          toast.error(
            'Saved, but the address list could not refresh. Close and reopen the appointment to see the latest.',
          );
        },
      },
    );
  };

  const handleEditCard = (addr: AddressListItem) => {
    const addrId = addr.id ?? null;
    // Flutter routing rule (calendar_screen.dart:803-822): residential
    // entries (or unset) open the address editor; non-residential
    // entries (emergency contacts) open the callback-contact editor.
    if (addr.isResidential === false && addrId) {
      setView({ kind: 'edit-callback', emergencyContactId: addrId });
    } else {
      setView({ kind: 'edit-address', selectedAddressId: addrId });
    }
  };

  const handleAddAddress = () => {
    setView({ kind: 'edit-address', selectedAddressId: null });
  };

  const handleModalCancel = () => setView({ kind: 'picker' });
  const handleModalSave = () => {
    setView({ kind: 'picker' });
    refreshFromPreBook();
  };

  // Modal sub-views replace the section content (Flutter pushes onto
  // leftPanelWidgets stack; React conditional-renders inside the same
  // host slot).
  if (view.kind === 'edit-address') {
    return (
      <EditResidentialAddressView
        patientId={patientId}
        appointmentSelectedAddressId={view.selectedAddressId}
        onCancel={handleModalCancel}
        onSave={handleModalSave}
      />
    );
  }
  if (view.kind === 'edit-callback') {
    return (
      <EditCallBackContactView
        patientId={patientId}
        emergencyContactId={view.emergencyContactId}
        onCancel={handleModalCancel}
        onSave={handleModalSave}
      />
    );
  }

  const isRefreshing = preBookPatient.isPending;

  return (
    <div className="px-4 py-3 border-t border-border-subtle">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <p className="text-xs font-medium text-text-muted">
            Address
          </p>
          {isRefreshing ? (
            <span
              className="inline-flex items-center gap-1 text-xs font-medium text-text-muted"
              aria-live="polite"
            >
              <Loader2 className="h-3 w-3 animate-spin" />
              Refreshing…
            </span>
          ) : null}
        </div>
        <button
          type="button"
          onClick={handleAddAddress}
          disabled={isRefreshing}
          className="inline-flex items-center gap-1 text-xs font-semibold text-brand-primary hover:underline disabled:opacity-50 disabled:no-underline"
        >
          <Plus className="h-3 w-3" />
          Add Address
        </button>
      </div>

      {visibleAddresses.length === 0 ? (
        <p className="text-sm text-text-muted py-2">
          No address added for this patient
        </p>
      ) : (
        <div className="space-y-2">
          {visibleAddresses.map((addr, i) => {
            const id = addr.id ?? '';
            const isSelected = !!id && id === selectedId;
            return (
              <div
                key={id || `addr-${i}`}
                className={cn(
                  'w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-colors',
                  isSelected
                    ? 'border-brand-primary bg-blue-100'
                    : 'border-border-default bg-surface-card hover:border-brand-primary',
                )}
              >
                <button
                  type="button"
                  onClick={() => id && select(id)}
                  className="flex-1 flex items-start gap-3 text-left min-w-0"
                >
                  <Home className="h-4 w-4 text-brand-primary flex-shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-text-primary truncate flex items-center gap-2">
                      {titleFor(addr)}
                      {addr.isDefault ? (
                        <span className="text-xs font-medium px-1 py-0.5 rounded bg-grey-100 text-text-muted">
                          Default
                        </span>
                      ) : null}
                    </p>
                    <p className="text-xs text-text-muted leading-snug">
                      {formatAddress(addr)}
                    </p>
                  </div>
                  {isSelected ? (
                    <Check className="h-4 w-4 text-brand-primary flex-shrink-0 mt-1" />
                  ) : null}
                </button>
                <button
                  type="button"
                  onClick={() => handleEditCard(addr)}
                  disabled={isRefreshing}
                  className="p-1 rounded-md text-text-secondary hover:bg-grey-100 flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                  aria-label="Edit address"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {selectedId ? (
        <button
          type="button"
          onClick={() => setValue('residentialAddressId', null, { shouldValidate: true })}
          className="mt-2 inline-flex items-center gap-1 text-xs text-status-error hover:underline"
        >
          <X className="h-3 w-3" />
          Clear selection
        </button>
      ) : null}
    </div>
  );
}

function titleFor(addr: AddressListItem): string {
  if (addr.isResidential) return addr.locationName?.trim() || 'Address';
  return addr.name?.trim() || 'Address';
}

/**
 * Build the body line shown under each picker card. Reads the four
 * wire fields that compose Flutter's `fullAddress` getter (Dart source:
 * `appointment_patient_address.dart:90-109`).
 *
 * Silent-drift canary (dev-mode only): an entry with an `id` but all
 * four address fields blank used to be the silent failure mode of the
 * OLD invented-field implementation. We now have data-shape safety on
 * the type side, but a backend rename of any of the four wire fields
 * would silently re-introduce empty-body cards. The dev-mode warning
 * here flags that drift the first time it appears.
 */
function formatAddress(addr: AddressListItem): string {
  const parts = [addr.apartment, addr.address, addr.city, addr.country]
    .map((p) => (p ?? '').trim())
    .filter((p) => p.length > 0);
  if (
    import.meta.env?.DEV &&
    parts.length === 0 &&
    !!(addr.id ?? '').trim() &&
    !__warnedEmptyAddresses.has(addr.id ?? '')
  ) {
    __warnedEmptyAddresses.add(addr.id ?? '');
    // eslint-disable-next-line no-console
    console.warn(
      '[ResidentialAddressSection] Address row has an id but every wire field ' +
        '(apartment/address/city/country) is blank — backend may have renamed a ' +
        'field. Inspect the PreBookSelectedPatient response.',
      { id: addr.id, addr },
    );
  }
  return parts.join(', ');
}

// Module-scope set so we warn once per id, not on every render.
const __warnedEmptyAddresses = new Set<string>();
